import { z } from "zod";
import { fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis, Refus } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const Schema = z.object({
  moisTenus: z.array(z.number()),
  totalDu: z.number(),
  totalDeclare: z.number(),
  totalPaye: z.number(),
  reste: z.number(),
  penalites: z.number(),
  detail: z.array(
    z.object({
      natureCode: z.string(),
      natureLibelle: z.string(),
      mois: z.number(),
      du: z.number().nullable(),
      declare: z.number(),
      paye: z.number(),
      penalite: z.number(),
      observation: z.string().nullable(),
    }),
  ),
});

export function Estimations({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["estimations", dossierId, annee],
    `/dossiers/${dossierId}/fiscal/estimations?annee=${annee}`,
    Schema,
  );

  if (q.isLoading) return <EtatChargement quoi="des estimations" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="Les estimations" />;

  const e = q.data.valeur;

  return (
    <div>
      <p className="resume">
        Ce qui est dû et déclarable <strong>sur les mois réellement tenus</strong>.
        Aucune projection : extrapoler l'année donnerait un chiffre qui ne
        correspondrait à <em>aucune déclaration à déposer</em>.
      </p>

      <section>
        <h2>Périmètre</h2>
        <p>
          {e.moisTenus.length} mois tenu(s) sur l'exercice {annee} :{" "}
          <strong>{e.moisTenus.map((m) => MOIS[m - 1]).join(", ")}</strong>.
        </p>
      </section>

      <Tuiles
        tuiles={[
          { titre: "Dû", valeur: fcfa(e.totalDu) },
          { titre: "Déclaré", valeur: fcfa(e.totalDeclare) },
          { titre: "Payé", valeur: fcfa(e.totalPaye) },
          { titre: "Reste", valeur: fcfa(e.reste), alerte: e.reste > 0 },
          { titre: "Pénalités", valeur: fcfa(e.penalites), alerte: e.penalites > 0 },
        ]}
      />

      <section>
        <h2>Détail par nature et par mois</h2>
        <Tableau
          colonnes={[
            { cle: "mois", titre: "Mois", rendu: (l) => MOIS[l.mois - 1] },
            { cle: "nat", titre: "Nature", rendu: (l) => l.natureLibelle },
            { cle: "du", titre: "Dû", montant: true, rendu: (l) => (l.du === null ? "—" : fcfa(l.du)) },
            { cle: "dec", titre: "Déclaré", montant: true, rendu: (l) => fcfa(l.declare) },
            { cle: "pay", titre: "Payé", montant: true, rendu: (l) => fcfa(l.paye) },
            {
              cle: "reste",
              titre: "Reste",
              montant: true,
              rendu: (l) => (l.du === null ? "—" : fcfa(Math.max(0, l.du - l.paye))),
            },
            { cle: "pen", titre: "Pénalité", montant: true, rendu: (l) => fcfa(l.penalite) },
            { cle: "obs", titre: "Observation", rendu: (l) => l.observation ?? "—" },
          ]}
          lignes={e.detail}
          cle={(l, i) => `${l.natureCode}-${l.mois}-${i}`}
          vide="Aucune ligne de suivi fiscal sur les mois tenus."
        />
      </section>

      <ListeAvis avis={q.data.avis} />
    </div>
  );
}
